/**
 * Блок «Сделки» (ТЗ п. 5.2.8, правила R-13, R-14).
 *
 * Раскрываются сделки за три года, предшествующие дате подачи. Выход
 * за период не блокирует генерацию, а даёт предупреждение (раздел 6.8).
 */

import { useEffect, useState } from 'react';
import { newRowId, type Deal } from '@bd/domain';
import { api } from '../../../api.js';
import { DataGrid } from '../../../components/DataGrid.js';
import { CheckboxField, kopecksToText, textToKopecks } from '../../../components/fields/index.js';
import type { BlockProps } from './types.js';
import { noteDictionaryFailure } from '../../../storage/dictionary-health.js';

export function DealsBlock({ profile, update, readOnly, errorFor }: BlockProps) {
  const deals = profile.deals;
  const [kinds, setKinds] = useState<string[]>([]);

  useEffect(() => {
    void api
      .dictionary('deal-kinds')
      .then((r) => setKinds(r.entries.map((e) => e.name)))
      .catch(noteDictionaryFailure);
  }, []);

  const rowErrors: Record<number, string[]> = {};
  deals.items.forEach((_, index) => {
    const keys = ['subject', 'date'].filter((key) => errorFor(`deals.items[${index}].${key}`));
    if (keys.length) rowErrors[index] = keys;
  });

  return (
    <section className="block">
      <h2>Сделки</h2>

      <CheckboxField
        label="В течение трёх лет совершались сделки с движимым или недвижимым имуществом"
        checked={deals.hasDeals}
        readOnly={readOnly}
        onChange={(v) => update((p) => ({ ...p, deals: { ...p.deals, hasDeals: v } }))}
      />

      {deals.hasDeals && (
        <DataGrid<Deal>
          rows={deals.items}
          readOnly={readOnly}
          errors={rowErrors}
          getRowId={(row) => row.id}
          onChange={(items) => update((p) => ({ ...p, deals: { ...p.deals, items } }))}
          createRow={() => ({ id: newRowId(), date: null, subject: '', kind: '', parties: '', price: 0, documentDetails: '' })}
          columns={[
            {
              key: 'date', title: 'Дата сделки', width: 12, type: 'date',
              get: (r) => r.date ?? '',
              set: (r, v) => ({ ...r, date: v || null }),
            },
            {
              key: 'kind', title: 'Вид сделки', width: 14, type: 'select', options: kinds,
              get: (r) => r.kind,
              set: (r, v) => ({ ...r, kind: v }),
            },
            { key: 'subject', title: 'Предмет', width: 24, get: (r) => r.subject, set: (r, v) => ({ ...r, subject: v }) },
            { key: 'parties', title: 'Стороны', width: 20, get: (r) => r.parties, set: (r, v) => ({ ...r, parties: v }) },
            {
              key: 'price', title: 'Цена, ₽', width: 14, type: 'money', align: 'right',
              get: (r) => kopecksToText(r.price),
              set: (r, v) => ({ ...r, price: textToKopecks(v) }),
            },
            { key: 'documentDetails', title: 'Реквизиты договора', width: 16, get: (r) => r.documentDetails, set: (r, v) => ({ ...r, documentDetails: v }) },
          ]}
        />
      )}

      {!deals.hasDeals && (
        <p className="hint">
          Заявление получит формулировку: «Сделок в течение трёхлетнего периода до даты подачи заявления в суд
          с движимым или недвижимым имуществом не совершал».
        </p>
      )}
    </section>
  );
}
